import "@/components/history/history.css";
import { historyWords as words } from "@/components/history/history-copy";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router";
import { Bento } from "@/components/ui/box";
import type { ListFacet } from "@/components/ui/box.schema";
import { useClassroomImport } from "@/components/imports/use-classroom-import";
import { useRunImportBox } from "@/components/imports/use-run-import-box";
import { useRegistrarRosterBox } from "@/components/imports/use-registrar-roster-box";
import { useRecordsFileBox } from "@/components/imports/use-records-file-box";
import { importsText as text, importsWords as iw } from "@/components/imports/imports-copy";
import { historyBox, historyRead } from "@/components/history/history-boxes";
import { importsApi, type ImportBatch, type ImportSummary } from "@/lib/api/imports";
import { runQuery, runRegisterRead, useRunPages, type KindFilter, type RunRegister, type RunsFilter } from "@/components/imports/run-register";
import { registerText as rt, registerWords as rw } from "@/components/imports/register-copy";
import { useTermCoverageBox } from "@/components/imports/use-term-coverage-box";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { PROTECTED_ROUTES } from "@/navigation/routes";
import { searchWithTrail } from "@/lib/visit/address";
import { RegistrarLocalNavigation } from "@/components/registrar/RegistrarLocalNavigation";

/** A run, or the register's last row while older runs remain unread. */
type RegisterRow = { run: ImportBatch } | { older: true };

export function ImportsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathId = useParams()["*"]?.split("/").filter(Boolean)[0];
  const legacyId = new URLSearchParams(location.search).get("batch");
  const classroom = useClassroomImport();
  // Under "import-batches", so an applied run reads the totals again.
  const summary = useQuery({ queryKey: ["import-batches", "summary"], queryFn: () => importsApi.getSummary() });
  const ranking = useQuery({ queryKey: ["imports-page-ranking"], queryFn: importsApi.ranking });
  const rank = (id: string) => ranking.data?.state === "ready" ? ranking.data.value.candidates.find((c) => c.id === id)?.rank ?? null : null;
  const courseImport = useRunImportBox(classroom.prepared, { keepOpen: classroom.keepOpen, parts: classroom.parts, batches: classroom.batchPart, blockers: classroom.blockers, rank: rank("history-import-course") });
  const recordsImport = useRecordsFileBox(rank("history-import-records"));
  const rosterImport = useRegistrarRosterBox(rank("history-import-roster"));
  const coverage = useTermCoverageBox();
  const [q, setQ] = useState("");
  const [runsFilter, setRunsFilter] = useState<RunsFilter>("apply");
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const find = useDebouncedValue(q, 300);
  const pages = useRunPages(runQuery(runsFilter, kindFilter, find));
  const narrowed = kindFilter !== "all" || find.trim() !== "";
  // Keep shared paths and older batch/receipt links pointing at their record.
  useEffect(() => {
    const runId = pathId || legacyId;
    if (!runId) return;
    const next = new URLSearchParams(location.search);
    next.delete("batch");
    navigate({ pathname: PROTECTED_ROUTES.admin.historicalImports,
      search: searchWithTrail(next.toString(), [`run:${runId}`]) }, { replace: true });
  }, [pathId, legacyId, navigate, location.search]);
  const facets: ListFacet[] = [
    { label: iw.mode, value: runsFilter, onChange: (v) => setRunsFilter(v as RunsFilter),
      options: [{ value: "apply", label: iw.appliedRuns }, { value: "dry_run", label: iw.dryRuns }, { value: "all", label: iw.allRuns }] },
    { label: iw.kind, value: kindFilter, onChange: (v) => setKindFilter(v as KindFilter),
      options: [{ value: "all", label: iw.allKinds }, { value: "historical", label: iw.closedTermRecords }, { value: "registrar_roster", label: iw.registrarRoster }] },
  ];
  const totals = historyBox({ kind: "fact", id: "import-totals", job: rw.totalsJob, label: rw.totals,
    owner: { system: "imam" }, read: historyRead(summary), fold: "never",
    detail: (v: ImportSummary) => rt.totalsDetail(v.last_applied_at),
    // No figure says that these totals ignore the register's filter, so the open head does.
    openDetail: (v: ImportSummary) => rt.totalsDetail(v.last_applied_at),
    figures: (v: ImportSummary) => [
      { value: v.applied_runs, unit: rt.appliedRuns(v.applied_runs) },
      { value: v.records_written, unit: rt.recordsWritten(v.records_written) },
      { value: v.refused_rows, unit: rt.refusedRows(v.refused_rows) },
      { value: v.dry_runs, unit: rt.dryRuns(v.dry_runs) },
    ],
  });
  const runs = historyBox({ kind: "list", id: "import-runs", job: words.findAnImportRunAndReadItsReceipts, label: words.importRuns,
    owner: { system: "imam" }, read: runRegisterRead(pages), fold: "never",
    detail: (v: RunRegister) => rt.runsShown(v.runs.length, v.more),
    state: (v: RunRegister) => v.reading ? { class: "reading", word: rw.readingRuns }
      : { class: "quiet", word: v.runs.length ? words.runsRecorded : rt.emptyWord(runsFilter, narrowed) },
    find: { placeholder: iw.findRun, onQueryChange: setQ, facets },
    // Paging belongs to the List: older runs are its last row, read in place (R6).
    rows: (v: RunRegister): RegisterRow[] => [...v.runs.map((run) => ({ run })), ...(v.more ? [{ older: true } as const] : [])],
    row: { key: (r) => "run" in r ? r.run.id : "older-runs", title: (r) => "run" in r ? r.run.source_name : rw.notReadYet,
      group: (r) => "run" in r ? rt.group(r.run.started_at) : rw.olderRuns,
      state: (r) => !("run" in r)
        ? pages.isFetchingNextPage ? { class: "reading", word: rw.readingOlderRuns } : pages.isFetchNextPageError ? { class: "failed", word: rw.olderRunsFailed } : null
        : r.run.status === "failed" ? { class: "failed", word: iw.failed }
          : r.run.status === "running" ? { class: "reading", word: iw.running }
            : r.run.rejected_count ? { class: "waiting", word: text.refusedRows(r.run.rejected_count) } : null,
      facts: (r) => "run" in r ? text.runFacts(r.run) : "",
      act: (r) => "run" in r ? { verb: words.readRun2, does: "visit", visit: `run:${r.run.id}` }
        : pages.isFetchingNextPage ? undefined
          : { verb: pages.isFetchNextPageError ? rw.tryAgain : rw.readOlderRuns, does: "check", run: () => { void pages.fetchNextPage(); } } },
  });
  return <div className="app-page">
    <RegistrarLocalNavigation area="history" />
    <Bento id="historical-imports" journey="Historical Imports" defaultOpen={[runs.id]}
      boxes={[recordsImport, rosterImport, ...classroom.boxes, ...(classroom.available ? [courseImport] : []), coverage, totals, runs]} />
  </div>;
}
